import { useEffect, useId, useRef, useState } from "react";
import { getApiUrl } from "../utils/api";
import { createPortal } from "react-dom";
import { FaCamera, FaTimes } from "react-icons/fa";
import CoverImageCropModal from "./CoverImageCropModal";
import { getPoemImageUrl } from "../utils/avatar";
import { getGroupCoverUrl, onGroupCoverError } from "../utils/cover";
import { isAllowedImageType, readFileAsDataUrl } from "../utils/imageCrop";

export default function EditGroupModal({
  open,
  groupId,
  initialName = "",
  initialTagline = "",
  initialDescription = "",
  initialJoinPolicy = "approval",
  initialCoverImage = "",
  coverVersion = 0,
  onClose,
  onSaved,
}) {
  const formId = useId();
  const coverInputRef = useRef(null);
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [description, setDescription] = useState("");
  const [joinPolicy, setJoinPolicy] = useState("approval");
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [removeCover, setRemoveCover] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setTagline(initialTagline);
    setDescription(initialDescription);
    setJoinPolicy(initialJoinPolicy === "open" ? "open" : "approval");
    setCoverFile(null);
    setRemoveCover(false);
    setError("");
    setCoverPreview((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return null;
    });
  }, [open, initialName, initialTagline, initialDescription, initialJoinPolicy]);

  useEffect(() => {
    return () => {
      if (coverPreview && coverPreview.startsWith("blob:")) {
        URL.revokeObjectURL(coverPreview);
      }
    };
  }, [coverPreview]);

  const existingCoverUrl = initialCoverImage
    ? `${getPoemImageUrl(initialCoverImage)}${
        getPoemImageUrl(initialCoverImage).includes("?") ? "&" : "?"
      }v=${coverVersion}`
    : getGroupCoverUrl(null);

  const displayCover = removeCover
    ? null
    : coverPreview || existingCoverUrl;

  const handleCoverFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!isAllowedImageType(file)) {
      setError("Please choose a JPEG, PNG, GIF, or WebP image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be under 5MB.");
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setCropImageSrc(dataUrl);
      setIsCropOpen(true);
      setError("");
    } catch {
      setError("Could not load that image. Please try another file.");
    }
  };

  const handleCoverCropComplete = (file, previewUrl) => {
    if (coverPreview && coverPreview.startsWith("blob:")) {
      URL.revokeObjectURL(coverPreview);
    }
    setCoverFile(file);
    setCoverPreview(previewUrl);
    setRemoveCover(false);
    setIsCropOpen(false);
    setCropImageSrc(null);
  };

  const handleCoverCropCancel = () => {
    setIsCropOpen(false);
    setCropImageSrc(null);
  };

  const handleRemoveCover = () => {
    if (coverPreview && coverPreview.startsWith("blob:")) {
      URL.revokeObjectURL(coverPreview);
    }
    setCoverPreview(null);
    setCoverFile(null);
    setRemoveCover(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      setError("Group name must be at least 2 characters.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const settingsRes = await fetch(
        getApiUrl(`/api/groups/${groupId}/settings`),
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            name: trimmedName,
            tagline: tagline.trim(),
            description: description.trim(),
            joinPolicy,
          }),
        }
      );
      const settingsData = await settingsRes.json();
      if (!settingsRes.ok) {
        throw new Error(settingsData.error || "Failed to update group");
      }

      if (removeCover) {
        const formData = new FormData();
        formData.append("removeCover", "true");
        const coverRes = await fetch(
          getApiUrl(`/api/groups/${groupId}/cover`),
          { method: "POST", credentials: "include", body: formData }
        );
        const coverData = await coverRes.json();
        if (!coverRes.ok) {
          throw new Error(coverData.error || "Failed to remove cover");
        }
      } else if (coverFile) {
        const formData = new FormData();
        formData.append("coverImage", coverFile);
        const coverRes = await fetch(
          getApiUrl(`/api/groups/${groupId}/cover`),
          { method: "POST", credentials: "include", body: formData }
        );
        const coverData = await coverRes.json();
        if (!coverRes.ok) {
          throw new Error(coverData.error || "Failed to update cover");
        }
      }

      onSaved?.({ coverChanged: removeCover || !!coverFile });
      onClose?.();
    } catch (err) {
      setError(err.message || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#333333]/50 p-4 overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget && !saving) onClose?.();
        }}
      >
        <div
          className="group-modal w-full max-w-md my-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`edit-group-title${formId}`}
        >
          <div className="group-modal__accent" aria-hidden="true" />
          <div className="group-modal__body">
            <h2 id={`edit-group-title${formId}`} className="group-modal__title">
              Edit group
            </h2>
            <p className="group-modal__subtitle">
              Update your group&apos;s details, cover, and join settings.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor={`edit-group-name${formId}`}
                  className="block text-sm font-semibold text-[#333333] mb-1"
                >
                  Group name
                </label>
                <input
                  id={`edit-group-name${formId}`}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="peer-home-input w-full px-3 py-2 text-sm"
                  maxLength={80}
                  required
                />
              </div>
              <div>
                <label
                  htmlFor={`edit-group-tagline${formId}`}
                  className="block text-sm font-semibold text-[#333333] mb-1"
                >
                  Tagline{" "}
                  <span className="font-normal text-[#777777]">(optional)</span>
                </label>
                <input
                  id={`edit-group-tagline${formId}`}
                  type="text"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  className="peer-home-input w-full px-3 py-2 text-sm"
                  maxLength={120}
                />
              </div>
              <div>
                <label
                  htmlFor={`edit-group-description${formId}`}
                  className="block text-sm font-semibold text-[#333333] mb-1"
                >
                  Description{" "}
                  <span className="font-normal text-[#777777]">(optional)</span>
                </label>
                <textarea
                  id={`edit-group-description${formId}`}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="peer-home-input w-full px-3 py-2 text-sm resize-y min-h-[80px]"
                  maxLength={500}
                />
              </div>
              <div>
                <span className="block text-sm font-semibold text-[#333333] mb-2">
                  Cover photo{" "}
                  <span className="font-normal text-[#777777]">(optional)</span>
                </span>
                {displayCover ? (
                  <div className="relative mb-2">
                    <img
                      src={displayCover}
                      alt="Group cover preview"
                      className="w-full h-36 object-cover rounded-lg border border-[#eeeeee]"
                      onError={(e) => {
                        if (!coverPreview) onGroupCoverError(e);
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveCover}
                      className="absolute top-2 right-2 bg-white/90 border border-[#eeeeee] rounded-full p-1.5 text-[#f26522] hover:bg-white"
                      aria-label="Remove cover"
                      disabled={saving}
                    >
                      <FaTimes size={12} />
                    </button>
                  </div>
                ) : null}
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handleCoverFileChange}
                  disabled={saving}
                />
                <button
                  type="button"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={saving}
                  className="peer-home-btn-secondary text-sm inline-flex items-center gap-2"
                >
                  <FaCamera aria-hidden="true" />
                  {displayCover ? "Change cover" : "Add cover"}
                </button>
              </div>
              <fieldset className="space-y-2 border-none m-0 p-0">
                <legend className="block text-sm font-semibold text-[#333333] mb-1">
                  Who can join?
                </legend>
                <label className="flex items-start gap-2 text-sm text-[#333333] cursor-pointer">
                  <input
                    type="radio"
                    name={`edit-join-policy${formId}`}
                    value="approval"
                    checked={joinPolicy === "approval"}
                    onChange={() => setJoinPolicy("approval")}
                    className="mt-1"
                    disabled={saving}
                  />
                  <span>
                    <span className="font-medium">Approval required</span>
                    <span className="block text-[#777777] text-xs mt-0.5">
                      You review each join request.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-[#333333] cursor-pointer">
                  <input
                    type="radio"
                    name={`edit-join-policy${formId}`}
                    value="open"
                    checked={joinPolicy === "open"}
                    onChange={() => setJoinPolicy("open")}
                    className="mt-1"
                    disabled={saving}
                  />
                  <span>
                    <span className="font-medium">Open group</span>
                    <span className="block text-[#777777] text-xs mt-0.5">
                      Logged-in poets can join instantly.
                    </span>
                  </span>
                </label>
              </fieldset>
              {error && <p className="text-sm text-red-700">{error}</p>}
              <div className="flex gap-2 justify-end pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="peer-home-btn-secondary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="peer-home-btn-primary disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <CoverImageCropModal
        open={isCropOpen}
        imageSrc={cropImageSrc}
        title="Crop group cover"
        fileNamePrefix="group_cover"
        onCancel={handleCoverCropCancel}
        onComplete={handleCoverCropComplete}
      />
    </>,
    document.body
  );
}
